import { push } from 'connected-react-router'
import { differenceInCalendarDays } from 'date-fns'
import * as Cookies from 'js-cookie'
import { all, call, put, select, take } from 'redux-saga/effects'

import { loadIsGroupOrderLocation, loadLocationById } from '../../api/api'
import { selectLocationRoute } from '../../routes/routes'
import { normalizeLocationErrorResponse } from '../../util/errorUtils'
import { setGTMBuilding } from '../../util/gtmUtils'
import { addGlobalEntities, toLocationSchema } from '../../util/snowplow'
import { SELECTED_KIOSK } from './locationSearch'
import { apiSaga } from './sagas'
import { selectCurrentLocationWithAddress, selectLocationDeliveryId } from './selectors'

// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const LOAD_LOCATION = 'foodsby/location/LOAD_LOCATION'
export const LOAD_LOCATION_SUCCESS = 'foodsby/location/LOAD_LOCATION_SUCCESS'
export const LOAD_LOCATION_FAILURE = 'foodsby/location/LOAD_LOCATION_FAILURE'
export const LOAD_PREVIOUS_LOCATION = 'foodsby/location/LOAD_PREVIOUS_LOCATION'
export const LOAD_PREVIOUS_LOCATION_SUCCESS = 'foodsby/location/LOAD_PREVIOUS_LOCATION_SUCCESS'
export const LOAD_PREVIOUS_LOCATION_FAILURE = 'foodsby/location/LOAD_PREVIOUS_LOCATION_FAILURE'
export const CLEAR_PREVIOUS_LOCATION = 'foodsby/location/CLEAR_PREVIOUS_LOCATION'
export const LOAD_IS_GROUP_ORDER_LOCATION = 'foodsby/location/LOAD_IS_GROUP_ORDER_LOCATION'
export const LOAD_IS_GROUP_ORDER_LOCATION_SUCCESS =
  'foodsby/location/LOAD_IS_GROUP_ORDER_LOCATION_SUCCESS'
export const LOAD_IS_GROUP_ORDER_LOCATION_FAILURE =
  'foodsby/location/LOAD_IS_GROUP_ORDER_LOCATION_FAILURE'

export const loadLocationStart = locationId => {
  return {
    payload: { locationId },
    type: LOAD_LOCATION,
  }
}

export const loadLocationSuccess = location => {
  return {
    payload: { location },
    type: LOAD_LOCATION_SUCCESS,
  }
}

export const loadLocationFailure = error => {
  return {
    error,
    type: LOAD_LOCATION_FAILURE,
  }
}

export const loadPreviousLocationStart = locationId => {
  return {
    payload: { locationId },
    type: LOAD_PREVIOUS_LOCATION,
  }
}

export const loadPreviousLocationSuccess = previousLocation => {
  return {
    payload: { previousLocation },
    type: LOAD_PREVIOUS_LOCATION_SUCCESS,
  }
}

export const loadPreviousLocationFailure = error => {
  return {
    error,
    type: LOAD_PREVIOUS_LOCATION_FAILURE,
  }
}

export const clearPreviousLocation = () => {
  return {
    type: CLEAR_PREVIOUS_LOCATION,
  }
}

export const loadIsGroupOrderLocationStart = locationId => {
  return {
    payload: { locationId },
    type: LOAD_IS_GROUP_ORDER_LOCATION,
  }
}

export const loadIsGroupOrderLocationSuccess = response => {
  return {
    payload: { response },
    type: LOAD_IS_GROUP_ORDER_LOCATION_SUCCESS,
  }
}

export const loadIsGroupOrderLocationFailure = error => {
  return {
    error,
    type: LOAD_IS_GROUP_ORDER_LOCATION_FAILURE,
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [CLEAR_PREVIOUS_LOCATION]: state => {
    return {
      ...state,
      previousLocation: undefined,
    }
  },
  [LOAD_IS_GROUP_ORDER_LOCATION]: state => {
    return {
      ...state,
      isGroupOrderLocationLoading: true,
    }
  },
  [LOAD_IS_GROUP_ORDER_LOCATION_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      isGroupOrderError: error,
      isGroupOrderLocation: undefined,
      isGroupOrderLocationLoading: false,
    }
  },
  [LOAD_IS_GROUP_ORDER_LOCATION_SUCCESS]: (state, action) => {
    const {
      payload: { response },
    } = action

    return {
      ...state,
      isGroupOrderLocation: response.supported,
      isGroupOrderLocationLoading: false,
    }
  },
  [LOAD_LOCATION]: state => {
    return {
      ...state,
      isLocationLoading: true,
    }
  },
  [LOAD_LOCATION_FAILURE]: (state, action) => {
    const error = normalizeLocationErrorResponse(action.error)
    return {
      ...state,
      isLocationLoading: false,
      location: undefined,
      locationError: error,
    }
  },
  [LOAD_LOCATION_SUCCESS]: (state, action) => {
    const {
      payload: { location },
    } = action
    return {
      ...state,
      isLocationLoading: false,
      location,
      locationError: undefined,
    }
  },
  [LOAD_PREVIOUS_LOCATION]: state => {
    return {
      ...state,
      isLocationLoading: true,
    }
  },
  [LOAD_PREVIOUS_LOCATION_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      error,
      isLocationLoading: false,
      previousLocation: undefined,
    }
  },
  [LOAD_PREVIOUS_LOCATION_SUCCESS]: (state, action) => {
    const {
      payload: { previousLocation },
    } = action
    previousLocation.address = {
      city: previousLocation.city,
      state: previousLocation.state,
      street: previousLocation.street,
      zip: previousLocation.zip,
    }
    return {
      ...state,
      error: undefined,
      isLocationLoading: false,
      previousLocation,
    }
  },
}

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  isLocationLoading: false,
  location: undefined,
}

export default function location(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

export function* watchLoadLocation() {
  while (true) {
    const {
      payload: { locationId },
    } = yield take(LOAD_LOCATION)
    const location = yield select(selectCurrentLocationWithAddress)
    if (!location || location.deliveryLocationId !== locationId) {
      yield call(
        apiSaga,
        loadLocationById,
        [locationId],
        loadLocationSuccess,
        loadLocationFailure,
        undefined,
      )
      yield put(loadIsGroupOrderLocationStart(locationId))
    } else {
      yield put(loadLocationSuccess(location))
    }
  }
}

export function* watchLoadLocationSuccess() {
  while (true) {
    const {
      payload: { location },
    } = yield take(LOAD_LOCATION_SUCCESS)

    addGlobalEntities([toLocationSchema(location.deliveryLocationId)])

    Cookies.set(
      'activeMinThirtyDays',
      differenceInCalendarDays(Date.now(), location.activationDate) > 30,
    )
    setGTMBuilding('location-loaded', location)
  }
}

export function* watchSelectedKiosk() {
  while (true) {
    const [
      // eslint-disable-next-line
      kiosk,
      {
        payload: { location },
      },
    ] = yield all([take(SELECTED_KIOSK), take(LOAD_LOCATION_SUCCESS)])
    setGTMBuilding('searchedBuilding', location)
  }
}

export function* watchLoadLocationFailure() {
  while (true) {
    const { error } = yield take(LOAD_LOCATION_FAILURE)
    if (Number(error.code) === 404) {
      yield put(push(selectLocationRoute.path))
    }
  }
}

export function* watchLoadPreviousLocation() {
  while (true) {
    const {
      payload: { locationId },
    } = yield take(LOAD_PREVIOUS_LOCATION)
    const currentLocationId = yield select(selectLocationDeliveryId)
    yield call(
      apiSaga,
      loadLocationById,
      [locationId],
      loadPreviousLocationSuccess,
      loadPreviousLocationFailure,
      undefined,
      true,
    )
    yield put(loadIsGroupOrderLocationStart(currentLocationId))
  }
}
export function* watchLoadGroupOrderLocation() {
  while (true) {
    const {
      payload: { locationId },
    } = yield take(LOAD_IS_GROUP_ORDER_LOCATION)
    try {
      yield call(
        apiSaga,
        loadIsGroupOrderLocation,
        [locationId],
        loadIsGroupOrderLocationSuccess,
        loadIsGroupOrderLocationFailure,
      )
    } catch (ex) {
      yield put(loadIsGroupOrderLocationFailure(ex))
    }
  }
}

export function* watchLoadIsGroupOrderLocationSuccess() {
  while (true) {
    const {
      payload: {
        response: { groupOrderOnly, supported, urlFriendlyName },
      },
    } = yield take(LOAD_IS_GROUP_ORDER_LOCATION_SUCCESS)

    if (supported) {
      if (window.appboy) {
        window.appboy.logCustomEvent('go-eligible')
      }
      if (groupOrderOnly) {
        window.location.href = `${process.env.REACT_APP_GROUP_ORDER_URL}/location/${urlFriendlyName}`
      }
    }
  }
}
